import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface PopoverPortalProps {
  /** Element the popover is positioned against (usually the trigger button). */
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  children: ReactNode;
  /** Gap in px between the anchor and the popover. */
  offset?: number;
}

/**
 * Renders `children` in a body-level portal, positioned under `anchorRef`.
 *
 * Popovers rendered inline are clipped by any ancestor with `overflow: hidden` —
 * notably `WorkspaceSectionCard`, the shared card wrapping every element-editor
 * section. Portaling to document.body escapes that ancestor entirely.
 *
 * Because the portal is out of normal flow, position is measured from the anchor
 * and kept in sync on scroll/resize.
 */
export default function PopoverPortal({
  anchorRef,
  onClose,
  children,
  offset = 8,
}: PopoverPortalProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const updatePosition = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const popover = popoverRef.current;
      const width = popover?.offsetWidth ?? 220;
      const height = popover?.offsetHeight ?? 300;

      // Flip above the anchor when there isn't room below.
      const spaceBelow = window.innerHeight - rect.bottom;
      const top =
        spaceBelow < height + offset && rect.top > height + offset
          ? rect.top - height - offset
          : rect.bottom + offset;

      // Keep the popover inside the viewport horizontally.
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));

      setPosition({ top, left });
    };

    updatePosition();

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [anchorRef, offset]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      {/* Click-outside catcher. Sits just under the popover. */}
      <div
        className="fixed inset-0 z-[1000]"
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />
      <div
        ref={popoverRef}
        className="fixed z-[1001]"
        style={{
          top: position?.top ?? 0,
          left: position?.left ?? 0,
          // Avoid a flash at 0,0 before the first measurement lands.
          visibility: position ? 'visible' : 'hidden',
        }}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
